/**
 * User Management — admin page for tenant users. Backed by:
 *   GET    /v1/users?page_size=&after=         → list (cursor-paginated)
 *   POST   /v1/users:invite                    → invite new user
 *   DELETE /v1/users/{membership_id}           → remove
 *   PATCH  /v1/users/{membership_id}/role      → change role
 *
 * Layout mirrors `ProvisioningTokensPage`: AdminViewHeader with an
 * "Invite user" action chip, stats bar, table with sticky header +
 * thin row borders. The invite form launches in an OverlayDialog
 * modal instead of rendering inline above the table.
 *
 * Role + remove actions live in each row, with the same two-step
 * "click → Confirm/Cancel" affordance the revoke flow uses so
 * destructive operations require an explicit second click. The
 * caller's own row hides destructive actions because the gateway
 * forbids self-remove / self-demote anyway and the no-op buttons
 * would only confuse.
 */
import { createPortal } from "react-dom";
import { ROLE_ACTIVITY_ACCESS_NOTE, ROLE_CATALOG, roleLabel } from "../lib/userRoles.js";
import { useCallback, useLayoutEffect, useDeferredValue, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import { restGet, restPost, restDelete, authedFetch } from "../lib/restClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import DataTable from "../components/DataTable.jsx";
import ColumnPicker from "../components/ColumnPicker.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import Select from "../components/Select.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { hasPermission, hasSensitivePermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import { ACTION_TEXT_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { formatCompact } from "../lib/renderers.js";
import { useCurrentUser } from "../lib/userInfo.js";
import EmptyState from "../components/EmptyState.jsx";
import AccessTab from "../components/identity/AccessTab.jsx";
import ActivityAccessTab from "../components/identity/ActivityAccessTab.jsx";
import UserEffectiveAccessDialog from "../components/identity/UserEffectiveAccessDialog.jsx";
import { listGrantRoles } from "../lib/accessClient.js";
import { listActivityGrants } from "../lib/activityConfiguration.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import { usesLocalAuth } from "../lib/authBootstrap.js";

// ── Schema ───────────────────────────────────────────────────────

const COLUMNS = [
    { id: "email",       label: "Email",      width: 240 },
    { id: "name",        label: "Name",       width: 180 },
    { id: "role",        label: "Role",       width: 180 },
    { id: "membership",  label: "Membership", width: 220 },
    { id: "_actions",    label: "Actions",    width: 220 },
];

export function getUserManagementColumns(canManageUsers, canInspectAccess = false) {
    return canManageUsers || canInspectAccess ? COLUMNS : COLUMNS.filter((c) => c.id !== "_actions");
}

const PAGE_SIZE = 100;

export { ROLE_CATALOG, roleLabel } from "../lib/userRoles.js";
// Picker offers every built-in. The backend writes the chosen kebab slug
// straight through to WorkOS (the role must be provisioned there — O-2335),
// so finer roles reach enforcement.
const ROLE_BY_SLUG = Object.fromEntries(ROLE_CATALOG.map((role) => [role.slug, role]));
const ROLE_OPTIONS = ROLE_CATALOG.map((r) => ({ value: r.slug, label: r.label }));

// Fallback for a membership whose slug doesn't resolve to a built-in —
// mirrors the backend `canonical_role_slug` default (the enforcement
// resolver's default role). The backend already collapses to this, so it's
// belt-and-braces for an older backend that omits `role_slug`.
const DEFAULT_ROLE_SLUG = "viewer";


// A roster read is not an action-validation surface: raw WorkOS/gateway
// details are neither actionable nor safe UI copy. This helper also gives the
// component test a stable seam for the failure state rendered below.
export function userListFailureMessage(err, caps) {
    return denialCopy(err, caps).message;
}

// ── Row shape ────────────────────────────────────────────────────

export function flattenUser(u) {
    const first = u.firstName ?? u.first_name ?? "";
    const last  = u.lastName  ?? u.last_name  ?? "";
    return {
        userId:        u.userId       ?? u.user_id       ?? "",
        membershipId:  u.membershipId ?? u.membership_id ?? "",
        email:         u.email ?? "",
        firstName:     first,
        lastName:      last,
        name:          [first, last].filter(Boolean).join(" "),
        // Canonical kebab RBAC slug. grpc-gateway camelCases proto field
        // names; tolerate either. If a backend predates the `role_slug`
        // field, fall back to the deprecated two-value enum so admins don't
        // silently render as Viewer during a version-skew window — only an
        // absent role entirely defaults to viewer.
        roleSlug:      u.roleSlug ?? u.role_slug ?? legacyRoleToSlug(u.role),
    };
}

// Map the deprecated two-value ProtoTenantRole to a canonical slug, for the
// version-skew fallback only (a backend that predates `role_slug`). Admin →
// tenant-admin; everything else (Member / unspecified / absent) → viewer.
function legacyRoleToSlug(role) {
    return role === "TENANT_ROLE_ADMIN" ? "tenant-admin" : DEFAULT_ROLE_SLUG;
}

// ── Cells ────────────────────────────────────────────────────────

function RoleBadge({ roleSlug }) {
    const entry = ROLE_BY_SLUG[roleSlug];
    // Tenant-admin is the only privileged-by-default role — keep the green
    // treatment it had as "Admin"; every finer role reads as neutral.
    const isAdmin = roleSlug === "tenant-admin";
    return (
        <span
            title={entry?.description ?? undefined}
            className={[
                "inline-flex items-center gap-[6px] rounded-[2px] border px-[8px] py-[2px] text-[11px] leading-[1.4]",
                isAdmin
                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                    : "border-[color:var(--line)] text-[color:var(--muted)]",
            ].join(" ")}
        >
            <span
                className={[
                    "h-[6px] w-[6px] flex-none rounded-[999px]",
                    isAdmin ? "bg-[color:var(--green)]" : "bg-[color:var(--muted-soft)]",
                ].join(" ")}
            />
            {roleLabel(roleSlug)}
        </span>
    );
}

// ── Inline invite form ───────────────────────────────────────────

export const EMPTY_ROSTER_NOTE = "Use “Invite user” to bring teammates onto this tenant.";

export function InviteForm({ onSubmit, busy, error, localAuth = usesLocalAuth() }) {
    const [email, setEmail] = useState("");
    const [roleSlug, setRoleSlug] = useState(DEFAULT_ROLE_SLUG);

    function submit(e) {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) return;
        onSubmit({ email: trimmed, roleSlug });
    }

    return (
        <form onSubmit={submit} className="grid gap-[12px]">
            <div className="grid gap-[10px] [grid-template-columns:minmax(0,2fr)_minmax(160px,1fr)] max-[600px]:grid-cols-1">
                <label className="grid gap-[4px]">
                    <span className={NOTE_CLASS}>Email</span>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="someone@example.com"
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                    />
                </label>
                <label className="grid gap-[4px]">
                    <span className={NOTE_CLASS}>Role</span>
                    <Select
                        value={roleSlug}
                        onChange={setRoleSlug}
                        options={ROLE_OPTIONS}
                        ariaLabel="Role"
                    />
                </label>
            </div>
            <span className={`${NOTE_CLASS} text-[10px]`}>
                {ROLE_BY_SLUG[roleSlug]?.description}
            </span>
            <span className={`${NOTE_CLASS} text-[10px]`}>{ROLE_ACTIVITY_ACCESS_NOTE}</span>
            <span className={`${NOTE_CLASS} text-[10px]`}>
                {localAuth
                    ? "The email must belong to an existing account. Adding the user gives that account access to this tenant. No invitation email is sent."
                    : "The user receives an invitation email and joins this tenant after accepting it."}
            </span>
            {error ? (
                <span className="text-[11px] text-red-400">{error}</span>
            ) : null}
            <div className="flex items-center justify-end">
                <button
                    type="submit"
                    disabled={busy}
                    className="h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.4] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer"
                >
                    {localAuth ? (busy ? "Adding..." : "Add user") : (busy ? "Inviting…" : "Send invite")}
                </button>
            </div>
        </form>
    );
}

export function InvitationSuccessMessage({ resent, localAuth = usesLocalAuth() }) {
    return (
        <span className={`${NOTE_CLASS} text-[12px] leading-[1.5]`}>
            {localAuth
                ? "The existing account has been added to this tenant. No invitation email was sent."
                : resent
                  ? "A previous pending invitation was replaced. The invitee must use the newest email link."
                  : "The recipient can access this tenant after accepting the invitation."}
        </span>
    );
}

// ── Page ─────────────────────────────────────────────────────────

export const USER_MANAGEMENT_TABS = [
    { id: "users", label: "Users", permission: PERMISSIONS.USERS_READ },
    { id: "access", label: "Grants", permission: PERMISSIONS.GRANTS_READ },
    { id: "activity-access", label: "Activity access", permission: PERMISSIONS.GRANTS_READ },
];

export function visibleUserManagementTabs(caps) {
    return USER_MANAGEMENT_TABS.filter((tab) => hasPermission(caps, tab.permission));
}

const probeLegacyAccessTransport = () => listGrantRoles({ pageSize: 1 });
const probeActivityAccessTransport = () => listActivityGrants();

function useAccessTransportAvailability(caps, probe) {
    const authIdentityEpoch = useSyncExternalStore(
        onAuthIdentityChange,
        getAuthIdentityEpoch,
        getAuthIdentityEpoch,
    );
    const eligible = hasPermission(caps, PERMISSIONS.GRANTS_READ);
    const [state, setState] = useState(() => ({ authIdentityEpoch, status: "pending" }));

    useEffect(() => {
        let cancelled = false;
        if (!eligible) {
            setState({ authIdentityEpoch, status: "unavailable" });
            return () => { cancelled = true; };
        }

        setState({ authIdentityEpoch, status: "pending" });
        probe()
            .then(() => {
                if (!cancelled) setState({ authIdentityEpoch, status: "ready" });
            })
            .catch(() => {
                if (!cancelled) setState({ authIdentityEpoch, status: "unavailable" });
            });
        return () => { cancelled = true; };
    }, [authIdentityEpoch, eligible, probe]);

    return state.authIdentityEpoch === authIdentityEpoch ? state.status : "pending";
}

function UsersTab({ viewDef, tabs, onOpenGrant, accessAvailable }) {
    const localAuth = usesLocalAuth();
    const me = useCurrentUser();
    const currentUserResolved = Boolean(me);
    // TenantUser.user_id is the WorkOS user ID, while /v1/me.user_id is
    // Origin's internal UUID. Match against the identity-provider subject so
    // the caller's own row cannot expose actions the gateway will reject.
    const myIdentitySub = me?.identitySub ?? null;

    // Role-change / remove / reset-password are all management actions.
    // Access inspection has its own read permissions and does not authorize
    // any of these management actions.
    // (Full multi-binding role editing is a follow-up; today this gates
    // the existing single-role affordances.)
    const caps = useMyPermissions();
    const capsRef = useRef(caps);
    capsRef.current = caps;
    const canManageUsers = hasPermission(caps, PERMISSIONS.USERS_MANAGE);
    const canInspectAccess = accessAvailable
        && hasSensitivePermission(caps, PERMISSIONS.GRANTS_READ)
        && hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const [accessUser, setAccessUser] = useState(null);
    useEffect(() => {
        if (!canInspectAccess) setAccessUser(null);
    }, [canInspectAccess]);

    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);

    const [overlay, setOverlay] = useState(null); // null | "invite" | "success"
    const [inviteBusy, setInviteBusy] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    // True when the backend revoked and replaced an existing pending invite,
    // so the success dialog can warn the invitee's earlier link is now dead.
    const [inviteResent, setInviteResent] = useState(false);

    // Per-row pending state. Map<membershipId, "remove-confirm" | "remove-pending"
    //   | "role-confirm" | "role-pending" | error string>.
    const [rowState, setRowState] = useState({});

    const setRowS = useCallback((id, value) => {
        setRowState((m) => {
            if (value == null) {
                const { [id]: _, ...rest } = m;
                return rest;
            }
            return { ...m, [id]: value };
        });
    }, []);

    const fetchPage = useCallback(async ({ replace = false, after = null } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page_size: PAGE_SIZE };
            if (after) params.after = after;
            const resp = await restGet("/v1/users", params);
            const list = (resp?.users ?? []).map(flattenUser);
            setRows((prev) => (replace ? list : [...prev, ...list]));
            // grpc-gateway camelCases proto field names; tolerate either.
            setNextCursor(resp?.nextCursor ?? resp?.next_cursor ?? null);
        } catch (err) {
            setError(userListFailureMessage(err, capsRef.current));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await fetchPage({ replace: true, after: null });
            if (cancelled) return;
        })();
        return () => { cancelled = true; };
    }, [fetchPage]);

    const handleInvite = useCallback(async ({ email, roleSlug }) => {
        setInviteBusy(true);
        setInviteError(null);
        try {
            // Send the canonical kebab slug; the backend writes it to WorkOS
            // (the legacy `role` enum is left unset — `role_slug` wins).
            const res = await restPost("/v1/users:invite", { email, roleSlug });
            setInviteResent(Boolean(res?.resent));
            setOverlay("success");
            // Refetch from the start so the new pending member shows
            // up if WorkOS surfaces them in the list immediately. Some
            // backends only show accepted users — refetching is still
            // the right thing because the gateway is the source of
            // truth.
            await fetchPage({ replace: true, after: null });
        } catch (err) {
            console.error("[user management] invite", err);
            setInviteError(denialOrRawMessage(err, caps));
        } finally {
            setInviteBusy(false);
        }
    }, [fetchPage, caps]);

    const handleRemove = useCallback(async (membershipId) => {
        setRowS(membershipId, "remove-pending");
        try {
            await restDelete(`/v1/users/${encodeURIComponent(membershipId)}`);
            setRows((prev) => prev.filter((r) => r.membershipId !== membershipId));
            setRowS(membershipId, null);
        } catch (err) {
            console.error("[user management] remove", err);
            setRowS(membershipId, `remove-failed: ${denialOrRawMessage(err, caps)}`);
        }
    }, [setRowS, caps]);

    // Admin-initiated password reset for another user. The proto
    // ChangePasswordRequest accepts an optional target_membership_id —
    // the backend validates that the membership belongs to the caller's
    // org before acting. current_password must be OMITTED on this path.
    const handleResetPw = useCallback(async (membershipId, newPassword) => {
        if (!newPassword) {
            setRowS(membershipId, "reset-pw-failed: new password required");
            return;
        }
        setRowS(membershipId, "reset-pw-pending");
        try {
            await restPost("/v1/user-management/change-password", {
                target_membership_id: membershipId,
                new_password: newPassword,
            });
            setRowS(membershipId, null);
        } catch (err) {
            console.error("[user management] reset password", err);
            setRowS(membershipId, `reset-pw-failed: ${denialOrRawMessage(err, caps)}`);
        }
    }, [setRowS, caps]);

    const handleRoleChange = useCallback(async (membershipId, newRoleSlug) => {
        setRowS(membershipId, "role-pending");
        try {
            // grpc-gateway's PATCH route binds the JSON body to the proto
            // message; send the canonical kebab slug as `roleSlug` so the
            // backend assigns the real RBAC role. authedFetch handles the
            // bearer + 401 refresh; restClient doesn't expose a named PATCH
            // helper, so call authedFetch directly.
            const res = await authedFetch(`/v1/users/${encodeURIComponent(membershipId)}/role`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ roleSlug: newRoleSlug }),
            });
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`PATCH /v1/users/.../role ${res.status}: ${text || res.statusText}`);
            }
            setRows((prev) =>
                prev.map((r) =>
                    r.membershipId === membershipId ? { ...r, roleSlug: newRoleSlug } : r,
                ),
            );
            setRowS(membershipId, null);
        } catch (err) {
            console.error("[user management] role change", err);
            setRowS(membershipId, `role-failed: ${denialOrRawMessage(err, caps)}`);
        }
    }, [setRowS, caps]);

    // Client-side filter over loaded rows. The /v1/users endpoint
    // doesn't accept a search filter (just cursor pagination), so —
    // same as the endpoint inventory — we filter what we have. Load
    // more pages to widen the candidate set.
    const filteredRows = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            if (r.email?.toLowerCase().includes(q)) return true;
            if (r.name?.toLowerCase().includes(q)) return true;
            if (r.firstName?.toLowerCase().includes(q)) return true;
            if (r.lastName?.toLowerCase().includes(q)) return true;
            // Match on both the human label ("SecOps") and the raw slug
            // ("sec-ops") so either spelling finds the row.
            if (roleLabel(r.roleSlug).toLowerCase().includes(q)) return true;
            if (r.roleSlug?.toLowerCase().includes(q)) return true;
            return false;
        });
    }, [rows, deferredSearch]);
    const isFiltering = deferredSearch.trim().length > 0;

    const tableColumns = useMemo(
        () => getUserManagementColumns(canManageUsers, canInspectAccess),
        [canManageUsers, canInspectAccess],
    );
    const cols = useDataTableColumns({
        storageKey: "userManagement",
        columns: tableColumns,
        defaultVisible: tableColumns.filter((column) => column.id !== "membership").map((column) => column.id),
    });

    const renderCell = useCallback(
        (r, col) => {
            switch (col.id) {
                case "email":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.email || "—"}
                            className="text-[11px] leading-[1.4] font-medium"
                        />
                    );
                case "name":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.name || "—"}
                            className="text-[11px] leading-[1.4] font-medium text-[color:var(--muted)]"
                        />
                    );
                case "role":
                    return <RoleBadge roleSlug={r.roleSlug} />;
                case "membership":
                    return (
                        <OverflowTooltip
                            as="span"
                            text={r.membershipId}
                            className="text-[11px] leading-[1.4] font-mono text-[color:var(--muted)]"
                        />
                    );
                case "_actions": {
                    if (!canManageUsers && !canInspectAccess) return null;
                    const state = rowState[r.membershipId];
                    const isSelf = myIdentitySub && r.userId === myIdentitySub;
                    return (
                        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
                            {canInspectAccess ? (
                                <button type="button" aria-haspopup="dialog" onClick={() => setAccessUser(r)} className="min-h-[34px] rounded-[4px] border border-[color:var(--line)] px-[12px] text-[11px] font-medium text-[color:var(--ink)] hover:border-[color:var(--green)] cursor-pointer">View access</button>
                            ) : null}
                            {canManageUsers && currentUserResolved ? <RowActions
                            row={r}
                            state={state}
                            isSelf={Boolean(isSelf)}
                            onRequestRoleChange={() => setRowS(r.membershipId, "role-confirm")}
                            onConfirmRoleChange={(slug) => handleRoleChange(r.membershipId, slug)}
                            onRequestRemove={() => setRowS(r.membershipId, "remove-confirm")}
                            onConfirmRemove={() => handleRemove(r.membershipId)}
                            onRequestResetPw={() => setRowS(r.membershipId, "reset-pw-entry")}
                            onConfirmResetPw={(pw) => handleResetPw(r.membershipId, pw)}
                            onCancel={() => setRowS(r.membershipId, null)}
                        /> : null}
                        </div>
                    );
                }
                default:
                    return null;
            }
        },
        [rowState, myIdentitySub, setRowS, handleRoleChange, handleRemove, handleResetPw, canManageUsers, canInspectAccess, currentUserResolved],
    );

    // Inviting is a management action — hide it without manage; the
    // column picker (a view preference) stays.
    const inviteAction = (
        <div className="flex items-center gap-[6px]">
            {canManageUsers ? (
                <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
                    <button
                        type="button"
                        onClick={() => {
                            setInviteError(null);
                            setOverlay((cur) => (cur === "invite" ? null : "invite"));
                        }}
                        aria-pressed={overlay === "invite"}
                        className="inline-flex items-center px-[10px] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
                    >
                        <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>
                            {localAuth ? "Add user" : "Invite user"}
                        </span>
                    </button>
                </div>
            ) : null}
            <ColumnPicker {...cols.pickerProps} />
        </div>
    );

    return (
        <>
            <AdminViewHeader
                title={viewDef?.label ?? "User Management"}
                scope="Organization-wide"
                tabs={tabs}
                actions={inviteAction}
            />
            <div className="flex flex-1 min-h-0 min-w-0 flex-col py-[8px]">
                <div className="shrink-0 grid gap-[10px] pb-[2px]">
                    <label className="grid gap-[5px]">
                        <span className={NOTE_CLASS}>Search</span>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Email, name, role…"
                            className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                        />
                    </label>

                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{formatCompact(filteredRows.length)}</strong>{" "}
                            {isFiltering ? "matching" : "shown"}
                        </span>
                        {isFiltering ? (
                            <span>
                                <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong> loaded
                            </span>
                        ) : null}
                        {nextCursor ? (
                            <span className={NOTE_CLASS}>(more available)</span>
                        ) : null}
                        {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                        {error ? <span className="text-red-400">{error}</span> : null}
                    </div>
                </div>

                <OverlayDialog
                    open={overlay === "invite"}
                    onClose={() => setOverlay(null)}
                    analyticsId={DIALOG.USER_INVITE}
                    eyebrow="Account"
                    title={localAuth ? "Add user" : "Invite user"}
                    width="560px"
                >
                    <InviteForm
                        onSubmit={handleInvite}
                        busy={inviteBusy}
                        error={inviteError}
                        localAuth={localAuth}
                    />
                </OverlayDialog>
                <OverlayDialog
                    open={overlay === "success"}
                    onClose={() => setOverlay(null)}
                    analyticsId={DIALOG.USER_INVITED}
                    eyebrow="Account"
                    title={localAuth ? "User added" : "Invitation sent"}
                    width="480px"
                >
                    <InvitationSuccessMessage resent={inviteResent} localAuth={localAuth} />
                </OverlayDialog>

                {accessUser && canInspectAccess ? (
                    <UserEffectiveAccessDialog key={accessUser.userId} user={accessUser} onOpenGrant={onOpenGrant} onClose={() => setAccessUser(null)} />
                ) : null}
                <DataTable
                    cols={cols}
                    rows={filteredRows}
                    getRowId={(r) => r.membershipId}
                    renderCell={renderCell}
                    loading={loading}
                    skeletonRows={4}
                    empty={
                        <EmptyState
                            className="my-[12px]"
                            title={error
                                ? "User list unavailable"
                                : isFiltering
                                  ? "No users match this search"
                                  : "No users in this tenant"}
                            note={error
                                ? error
                                : isFiltering
                                  ? "Clear the search or load more pages to widen the result set."
                                  : localAuth ? 'Use "Add user" to add an existing account to this tenant.' : EMPTY_ROSTER_NOTE}
                        />
                    }
                    footer={
                        nextCursor ? (
                            <div className="grid place-items-center pt-[8px] pb-[10px]">
                                <button
                                    type="button"
                                    onClick={() => fetchPage({ after: nextCursor })}
                                    disabled={loading}
                                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.4] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                                >
                                    {loading ? "Loading…" : "Load more"}
                                </button>
                            </div>
                        ) : null
                    }
                />
            </div>
        </>
    );
}

export default function UserManagementPage({ viewDef }) {
    const [selectedGrant, setSelectedGrant] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const authIdentityEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    useEffect(() => setSelectedGrant(null), [authIdentityEpoch]);
    const caps = useMyPermissions();
    const legacyAccessTransport = useAccessTransportAvailability(caps, probeLegacyAccessTransport);
    const activityAccessTransport = useAccessTransportAvailability(caps, probeActivityAccessTransport);
    const visibleTabs = useMemo(
        () => visibleUserManagementTabs(caps).filter((tab) => {
            if (tab.id === "access") return legacyAccessTransport === "ready";
            if (tab.id === "activity-access") return activityAccessTransport === "ready";
            return true;
        }),
        [activityAccessTransport, caps, legacyAccessTransport],
    );
    const visibleIds = useMemo(() => new Set(visibleTabs.map((tab) => tab.id)), [visibleTabs]);
    const requested = searchParams.get("utab");
    const requestedTransport = requested === "access"
        ? legacyAccessTransport
        : requested === "activity-access"
          ? activityAccessTransport
          : "ready";
    const active = requested && visibleIds.has(requested)
        ? requested
        : (visibleTabs[0]?.id ?? "users");

    useEffect(() => {
        if (requestedTransport === "pending") return;
        if (!visibleTabs.length || requested === active || (!requested && active === "users")) return;
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (active === "users") next.delete("utab");
            else next.set("utab", active);
            return next;
        }, { replace: true });
    }, [active, requested, requestedTransport, setSearchParams, visibleTabs.length]);

    const selectTab = (id) => {
        setSelectedGrant(null);
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (id === "users") next.delete("utab");
            else next.set("utab", id);
            return next;
        }, { replace: true });
    };
    const tabs = visibleTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        active: tab.id === active,
        dot: tab.id === active ? "bg-[color:var(--green)]" : undefined,
        onSelect: () => selectTab(tab.id),
    }));

    if (requestedTransport === "pending") {
        return (
            <>
                <AdminViewHeader title={viewDef?.label ?? "User Management"} tabs={tabs} />
                <div className="p-[12px] text-[11px] text-[color:var(--muted)]" aria-busy="true">
                    Checking access-management availability...
                </div>
            </>
        );
    }

    if (active === "access") {
        return (
            <>
                <AdminViewHeader title={viewDef?.label ?? "User Management"} tabs={tabs} />
                <AccessTab key={authIdentityEpoch} selectedGrant={selectedGrant} onClearSelectedGrant={() => setSelectedGrant(null)} />
            </>
        );
    }
    if (active === "activity-access") {
        return (
            <>
                <AdminViewHeader title={viewDef?.label ?? "User Management"} tabs={tabs} />
                <ActivityAccessTab key={authIdentityEpoch} />
            </>
        );
    }
    return <UsersTab key={authIdentityEpoch} onOpenGrant={() => {
        setSelectedGrant(null);
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set("utab", "activity-access");
            return next;
        });
    }} viewDef={viewDef} tabs={tabs} accessAvailable={legacyAccessTransport === "ready"} />;
}

// ── Row actions ──────────────────────────────────────────────────

export function UserActionForm({
    row,
    state,
    isSelf,
    onConfirmRoleChange,
    onConfirmRemove,
    onConfirmResetPw,
    onCancel,
}) {
    // Local-only entry field for the admin reset-password flow. Parent
    // owns the state machine (rowState), but the password is never
    // hoisted there — it lives only in the row that's actively
    // accepting it. Reset when leaving the entry state.
    const [pw, setPw] = useState("");
    useEffect(() => {
        if (state !== "reset-pw-entry") setPw("");
    }, [state]);

    // Local-only target-role selection for the change-role flow, seeded to
    // the row's current role so "Apply" is a no-op until the admin picks a
    // different one. Reset when leaving the confirm state.
    const [pendingRole, setPendingRole] = useState(row.roleSlug);
    useEffect(() => {
        if (state === "role-confirm") setPendingRole(row.roleSlug);
    }, [state, row.roleSlug]);

    if (isSelf) {
        return (
            <span className={`${NOTE_CLASS}`}>You</span>
        );
    }
    if (state === "remove-confirm") {
        return (
            <span className="flex items-center gap-[4px]">
                <span className={`${NOTE_CLASS} mr-[4px]`}>Remove?</span>
                <button
                    type="button"
                    onClick={onConfirmRemove}
                    className="text-[11px] text-red-400 hover:text-red-300 cursor-pointer"
                >
                    Confirm
                </button>
                <span className="text-[color:var(--muted-soft)]">·</span>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                    Cancel
                </button>
            </span>
        );
    }
    if (state === "role-confirm") {
        const unchanged = pendingRole === row.roleSlug;
        return (
            <span className="grid max-w-[360px] gap-[6px]">
                <span className="flex items-center gap-[4px]">
                    <span className="min-w-[150px]">
                        <Select
                            value={pendingRole}
                            onChange={setPendingRole}
                            options={ROLE_OPTIONS}
                            ariaLabel="New role"
                        />
                    </span>
                    <button
                        type="button"
                        onClick={() => onConfirmRoleChange(pendingRole)}
                        disabled={unchanged}
                        title={unchanged ? "Pick a different role to apply" : undefined}
                        className="text-[11px] text-[color:var(--ink)] hover:text-[color:var(--green)] disabled:opacity-40 cursor-pointer"
                    >
                        Apply
                    </button>
                    <span className="text-[color:var(--muted-soft)]">·</span>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        Cancel
                    </button>
                </span>
                <span className={NOTE_CLASS}>{ROLE_BY_SLUG[pendingRole]?.description}</span>
                <span className={NOTE_CLASS}>{ROLE_ACTIVITY_ACCESS_NOTE} Changing this role does not change activity grants.</span>
            </span>
        );
    }
    if (state === "reset-pw-entry") {
        const commit = () => { if (pw) onConfirmResetPw(pw); };
        return (
            <span className="flex items-center gap-[4px]">
                <input
                    type="password"
                    autoFocus
                    value={pw}
                    placeholder="New password"
                    onChange={(e) => setPw(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); commit(); }
                        else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
                    }}
                    className="min-h-[24px] w-[110px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[6px] text-[11px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                />
                <button
                    type="button"
                    onClick={commit}
                    disabled={!pw}
                    className="text-[11px] text-[color:var(--ink)] hover:text-[color:var(--green)] disabled:opacity-40 cursor-pointer"
                >
                    Set
                </button>
                <span className="text-[color:var(--muted-soft)]">·</span>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                    Cancel
                </button>
            </span>
        );
    }
    if (state === "remove-pending") {
        return <span className={`${NOTE_CLASS}`}>Removing…</span>;
    }
    if (state === "role-pending") {
        return <span className={`${NOTE_CLASS}`}>Updating…</span>;
    }
    if (state === "reset-pw-pending") {
        return <span className={`${NOTE_CLASS}`}>Resetting…</span>;
    }
    if (typeof state === "string" && state.startsWith("remove-failed")) {
        return <span className="text-[11px] text-red-400" title={state}>Remove failed</span>;
    }
    if (typeof state === "string" && state.startsWith("role-failed")) {
        return <span className="text-[11px] text-red-400" title={state}>Update failed</span>;
    }
    if (typeof state === "string" && state.startsWith("reset-pw-failed")) {
        return <span className="text-[11px] text-red-400" title={state}>Reset failed</span>;
    }
    return null;
}


function RowActions(props) {
    const [open, setOpen] = useState(false);
    const trigger = useRef(null);
    const form = useRef(null);
    const previousState = useRef(props.state);
    useLayoutEffect(() => {
        if (props.state && previousState.current !== props.state) {
            form.current?.querySelector('input, button:not(:disabled)')?.focus();
        } else if (previousState.current && !props.state) {
            trigger.current?.focus();
        }
        previousState.current = props.state;
    }, [props.state]);
    const menu = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const box = trigger.current?.getBoundingClientRect();
            if (box) setPosition({ top: Math.max(8, Math.min(box.bottom + 4, window.innerHeight - 150)), left: Math.max(8, Math.min(box.right - 190, window.innerWidth - 198)) });
        };
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
    }, [open]);
    useEffect(() => {
        if (!open) return;
        menu.current?.querySelector('[role="menuitem"]')?.focus();
        const outside = (event) => {
            if (!menu.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("pointerdown", outside);
        return () => document.removeEventListener("pointerdown", outside);
    }, [open]);
    if (props.isSelf || props.state) return <span ref={form}><UserActionForm {...props} /></span>;
    const choose = (action) => { setOpen(false); action(); };
    return <>
        <button ref={trigger} type="button" aria-label={`More actions for ${props.row.email}`} aria-haspopup="menu" aria-expanded={open}
            className="h-[34px] min-w-[34px] rounded-[4px] text-[18px] text-[color:var(--muted)] hover:bg-[color:var(--control-bg)] cursor-pointer"
            onClick={() => setOpen((value) => !value)} onKeyDown={(event) => {
                if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); }
            }}>...</button>
        {open ? createPortal(<div ref={menu} role="menu" aria-label={`Actions for ${props.row.email}`} style={position}
            className="fixed z-[10001] grid w-[190px] gap-[2px] rounded-[5px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[5px] shadow-lg"
            onKeyDown={(event) => {
                const items = [...menu.current.querySelectorAll('[role="menuitem"]')];
                const index = items.indexOf(document.activeElement);
                if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                    event.preventDefault();
                    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
                    items[next]?.focus();
                } else if (event.key === "Escape" || event.key === "Tab") {
                    event.preventDefault(); setOpen(false); trigger.current?.focus();
                }
            }}>
            {[ ["Change role", props.onRequestRoleChange], ["Reset password", props.onRequestResetPw], ["Remove", props.onRequestRemove] ].map(([label, action]) =>
                <button key={label} role="menuitem" type="button" className={`min-h-[38px] rounded-[3px] px-[10px] text-left text-[11px] hover:bg-[color:var(--control-bg)] focus:bg-[color:var(--control-bg)] ${label === "Remove" ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--ink)]"}`} onClick={() => choose(action)}>{label}</button>)}
        </div>, document.body) : null}
    </>;
}
